import React, { useEffect, useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fetchEntries } from '../contentfulClient';
import SEOHead from './SEOHead';
import StructuredData from './StructuredData';

// Display order for the work grid — scenes and environments first, props after.
// Anything published in Contentful but not listed here is appended, so a new
// artwork never disappears just because this list went stale.
const ORDERED_SLUGS = [
  'akihabara-3d-model',
  'bic-camera-store-akihabara-tokyo',
  'tokyo-rustic-house',
  'small-japanese-tobacco-shop-tokyo',
  'japanese-tonkatsu-shop-sign',
  '8-japanese-gashapon-capsule-toy-machines',
  'japanese-coca-cola-vending-machine-pack',
  'ito-en-japanese-vending-machine-light-green',
  'japanese-vending-machine',
  'gashapon-toy-capsule-vending-machine',
  'mcdonalds-self-service-kiosk',
  'traffic-blockers-pack',
  'atm-wincor-nixdorf-cineo-customized-worn',
  'tesla-supercharger-ev-charging-station',
  'old-desktop-computer-tower-pc-retro-2000s-office',
  'iphone-17-pro-smartphone',
];


// Per-slug crop focus for the 1:1 grid tiles ('top'|'bottom'|'left'|'right'|'center')
const FOCAL_OVERRIDES = {};
const focalFor = (slug) => FOCAL_OVERRIDES[slug] || 'center';

// Column count per breakpoint — must match the grid classes below.
const COLS = { base: 2, sm: 3, lg: 6 };
const HEADLINE_SPAN = 2;

// Tailwind cannot compile interpolated class names — spans come from static maps.
const FILL_BASE = { 1: 'col-span-1', 2: 'col-span-2' };
const FILL_SM = { 1: 'sm:col-span-1', 2: 'sm:col-span-2', 3: 'sm:col-span-3' };
const FILL_LG = {
  1: 'lg:col-span-1', 2: 'lg:col-span-2', 3: 'lg:col-span-3',
  4: 'lg:col-span-4', 5: 'lg:col-span-5', 6: 'lg:col-span-6',
};

// How many cells the closing tile must take so the last row has no hole.
// 0 means the grid already ends flush at that breakpoint.
const fillFor = (count, cols) => {
  const rem = (HEADLINE_SPAN + count) % cols;
  return rem === 0 ? 0 : cols - rem;
};

const artUrl = (art) => {
  const raw = art?.fields?.image?.fields?.file?.url || '';
  return raw.startsWith('//') ? `https:${raw}` : raw;
};

// Blur-up placeholder painted under every image plate
const lqipStyle = (url) => (url ? {
  backgroundImage: `url(${url}?w=24&q=20&fm=webp)`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
} : undefined);

const PLATE = 'bg-[#141414] overflow-hidden';
const PANEL = 'bg-[#101010] ring-1 ring-inset ring-white/[0.07]';
const KICKER = 'text-[10.5px] tracking-[0.26em] uppercase text-white/50';
const FOOT_LINK = 'text-white/65 hover:text-white border-b border-white/25 hover:border-white pb-0.5 transition-colors';

const Portfolio = () => {
  const [artworks, setArtworks] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const activeFilter = searchParams.get('filter') || 'All';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setArtworks(await fetchEntries());
      } catch (err) {
        console.error('Error fetching content:', err);
        setError('Failed to load artworks');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Curated order first, then anything the list doesn't know about.
  const orderedWork = useMemo(() => {
    const bySlug = {};
    artworks.forEach((a) => { if (a.fields.slug) bySlug[a.fields.slug] = a; });
    const listed = ORDERED_SLUGS.map((slug) => bySlug[slug]).filter(Boolean);
    const seen = new Set(ORDERED_SLUGS);
    return [...listed, ...artworks.filter((a) => !seen.has(a.fields.slug))];
  }, [artworks]);

  const gridWork = useMemo(() => {
    if (activeFilter === 'All') return orderedWork;
    return orderedWork.filter((a) => (a.fields.tags || []).includes(activeFilter));
  }, [orderedWork, activeFilter]);

  const n = gridWork.length;
  const fillBase = fillFor(n, COLS.base);
  const fillSm = fillFor(n, COLS.sm);
  const fillLg = fillFor(n, COLS.lg);
  const needsFiller = fillBase > 0 || fillSm > 0 || fillLg > 0;
  const fillerClasses = [
    fillBase ? `${FILL_BASE[fillBase]} block` : 'hidden',
    fillSm ? `${FILL_SM[fillSm]} sm:block` : 'sm:hidden',
    fillLg ? `${FILL_LG[fillLg]} lg:block` : 'lg:hidden',
  ].join(' ');

  const portfolioSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Gianluca Gatto",
    "jobTitle": "3D Artist & Blender Specialist",
    "description": "3D artist making photorealistic environments and game-ready props in Blender — shopfronts, vending machines, signage and street furniture.",
    "url": "https://gattogianluca.com/",
    "sameAs": [
      "https://www.youtube.com/@Gianluca3D",
      "https://www.turbosquid.com/Search/Artists/GianlucaGatto",
      "https://www.cgtrader.com/designers/gianluca-gatto",
      "https://sketchfab.com/shockproof"
    ],
    "knowsAbout": ["3D Modeling", "Blender", "Environment Art", "Game Assets", "Product Visualization", "PBR Texturing"]
  };

  if (error) {
    return (
      <motion.div className="text-red-500 text-center mt-8" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {error}
      </motion.div>
    );
  }

  return (
    <>
      <SEOHead
        title="3D Environment & Prop Models — Gianluca Gatto"
        description="Photorealistic 3D environments and game-ready props — shopfronts, vending machines, signage and street furniture, modelled in Blender."
        keywords="3D environment models, game ready props, photorealistic 3D models, PBR props, vending machine 3D model, street furniture 3D model, Blender environment art"
        noindex={activeFilter !== 'All'}
      />
      <StructuredData data={portfolioSchema} />

      <motion.div
        className="min-h-[calc(100vh-var(--nav-h))] bg-[#0B0B0B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.13, ease: "easeOut" }}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-8 pb-6 sm:pb-8">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">

            {/* ── Headline tile — the h1 lives in the grid, so naming the work costs no extra screen ── */}
            <div className={`col-span-2 ${PANEL} h-full min-h-[170px] p-4 sm:p-5 flex flex-col justify-between gap-3`}>
              {activeFilter === 'All' ? (
                <>
                  <p className={KICKER}>3D Artist &middot; Blender</p>
                  <h1
                    className="font-display font-extrabold text-white leading-[1.06] tracking-[-0.015em] text-[clamp(1.15rem,1.72vw,1.45rem)]"
                    style={{ textWrap: 'balance' }}
                  >
                    Photorealistic 3D environments and game-ready props.
                  </h1>
                  <p className="text-[12px] sm:text-[12.5px] leading-snug text-white/65">
                    Shopfronts, vending machines, signage and street furniture, modelled from photo reference in Blender.
                  </p>
                </>
              ) : (
                <>
                  <p className={KICKER}>Filtered</p>
                  <h1 className="font-display font-extrabold text-white leading-[1.06] text-[clamp(1.15rem,1.72vw,1.45rem)]">
                    {activeFilter}
                  </h1>
                  <Link to="/" className="text-[12.5px] text-white/65 hover:text-white border-b border-white/25 hover:border-white pb-0.5 self-start transition-colors">
                    Show all work
                  </Link>
                </>
              )}
            </div>

            {/* ── Work tiles ── */}
            {loading
              ? [...Array(10)].map((_, i) => <div key={`sk-${i}`} className={`aspect-square ${PLATE}`} />)
              : gridWork.map((artwork, index) => {
                  const slug = artwork.fields.slug;
                  const url = artUrl(artwork);
                  const focal = focalFor(slug);
                  return (
                    <Link
                      key={`work-${slug || index}`}
                      to={`/artworks/${slug}/`}
                      className={`group relative block aspect-square ${PLATE} focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0B]`}
                      style={lqipStyle(url)}
                    >
                      <img
                        src={`${url}?w=800&h=800&fit=fill&f=${focal}&fm=webp&q=85`}
                        srcSet={`${url}?w=400&h=400&fit=fill&f=${focal}&fm=webp&q=85 400w, ${url}?w=800&h=800&fit=fill&f=${focal}&fm=webp&q=85 800w, ${url}?w=1200&h=1200&fit=fill&f=${focal}&fm=webp&q=85 1200w`}
                        sizes="(max-width: 639px) 50vw, (max-width: 1023px) 33vw, (max-width: 1400px) 17vw, 220px"
                        alt={`${artwork.fields.title} - 3D model by Gianluca Gatto`}
                        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                        loading={index < 12 ? 'eager' : 'lazy'}
                        fetchpriority={index < 6 ? 'high' : 'auto'}
                        decoding="async"
                        width={800}
                        height={800}
                      />
                      {/* Hairline sits ABOVE the image — ring-inset on the parent paints under it */}
                      <span className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-white/[0.07]" aria-hidden="true" />
                      <span className="absolute inset-x-0 bottom-0 px-3 pb-3 pt-12 bg-gradient-to-t from-black/95 via-black/75 to-transparent opacity-0 translate-y-1 transition-all duration-300 group-hover:opacity-100 group-hover:translate-y-0 group-focus-visible:opacity-100 group-focus-visible:translate-y-0">
                        <span className="block font-display font-bold text-white text-[12px] sm:text-[12.5px] leading-tight">
                          {artwork.fields.title}
                        </span>
                      </span>
                    </Link>
                  );
                })}

            {/* ── Closing tile — only rendered where the last row would otherwise have a hole ── */}
            {!loading && needsFiller && (
              <div className={`${fillerClasses} ${PANEL} h-full min-h-[120px] p-5 sm:p-6 flex flex-col justify-end`}>
                <p className={`${KICKER} mb-3`}>Custom work</p>
                <Link
                  to="/contact/"
                  className="font-display font-bold text-white text-sm hover:text-white/80 transition-colors self-start border-b border-white/30 hover:border-white pb-0.5"
                >
                  Start a commission &rarr;
                </Link>
              </div>
            )}
          </div>

          {!loading && gridWork.length === 0 && (
            <p className="text-white/50 text-sm text-center py-16">No work matches this filter.</p>
          )}

          {/* ── One quiet line under the grid: licensing and contact, after the work, not before it ── */}
          <div className="mt-7 flex justify-end">
            <a href="mailto:email@gattogianluca.com" className={`text-[11.5px] inline-block py-1 ${FOOT_LINK}`}>
              email@gattogianluca.com
            </a>
          </div>
        </div>
      </motion.div>
    </>
  );
};

export default Portfolio;
